import { ensurePackageBundled, addPackageOverride, errorEntrySource, type Context } from '@ms-cloudpack/api-server';
import type { Request, Response } from '@ms-cloudpack/create-express-app';
import { parseRequestInfo } from '@ms-cloudpack/import-map';
import { findResolveMapEntry } from '@ms-cloudpack/package-utilities';
import { normalizeRelativePath, slash } from '@ms-cloudpack/path-string-parsing';
import path from 'path';
import type { BundleServerOptions } from './types/BundleServerOptions.js';
import { handleWorker } from './handleWorker.js';
import { rewriteImportsFromFile } from './rewriteImports.js';
import { getLinkedPackageRedirectUrl } from './getLinkedPackageRedirectUrl.js';
import { logResponse, sendErrorResponse } from './responseHelpers.js';

/** File read errors have a `code` like `ENOENT`. HTTP errors from `sendFile` have a `statusCode`. */
type ErrorWithCodes = Error & { statusCode?: number; code?: string };

const maxAge = 31536000; // 1 year

export async function handleBundleRequest(
  options: BundleServerOptions & {
    req: Request;
    res: Response;
  },
  context: Context,
): Promise<void> {
  const { req, res, disableCache } = options;
  const { session, packageHashes, bus, telemetryClient } = context;
  const { resolveMap, config, linkedPaths } = session;
  const { features = {} } = config;
  const requestPath = slash(req.path.substring(1));
  const force = req.query.force !== undefined;
  const worker = req.query.worker !== undefined;
  const { packageName, version, hash, bundled, filePath, missing } = parseRequestInfo(requestPath);

  console.debug(`Bundle request: ${requestPath}`);

  // If the package is missing, invalid arguments.
  if (!requestPath || !packageName) {
    return sendErrorResponse({
      res,
      status: 400,
      requestPath,
      message: `Requests must be in the format "/{packageName}@{version}/{type}/path/file.ext"`,
    });
  }

  const packageEntry = findResolveMapEntry({ packageName, version, resolveMap });
  if (!packageEntry) {
    // If the package is missing, invalid arguments.
    return sendErrorResponse({
      res,
      status: 404,
      requestPath,
      message: `Unrecognized package "${packageName}@${version || '(unknown)'}"`,
    });
  }

  // This is a hot path, so doing a quick check first.
  if (linkedPaths.length) {
    // Try to redirect to remote host if necessary.
    const redirectUrl = getLinkedPackageRedirectUrl({
      linkedPaths,
      packageName,
      version,
      packageEntry,
      originalUrl: req.originalUrl,
    });

    if (redirectUrl) {
      return sendRedirect({ res, requestPath, message: `Redirecting to linked package ${redirectUrl}`, redirectUrl });
    }
  }

  // If the version is missing, redirect to the primary version.
  if (!version) {
    return sendRedirect({
      res,
      requestPath,
      message: `Redirecting versionless request to primary version ${packageEntry.version}`,
      redirectUrl: `/${packageName}@${packageEntry.version}${filePath}`,
    });
  }

  const packageRequestPath = `${packageName}@${version}${filePath}`;

  if (missing && features.autoUpdateEntries) {
    // Exports map for this package is missing, so try adding an override for the requested file.
    const addedPackageOverride = await addPackageOverride({
      input: { packageName, version, importPath: filePath, initiatedBy: 'autoUpdateEntries' },
      ctx: context,
    });

    if (addedPackageOverride) {
      if (features.staticAnalysisInit) {
        const warningMessage =
          `⚠️ Static analysis did not detect that "${packageName}${filePath}" is imported. ` +
          `The dependency was resolved at runtime via on-demand bundling. ` +
          `If this happens frequently, consider disabling "staticAnalysisInit" in your cloudpack config.`;
        console.warn(warningMessage);
        bus.publish(errorEntrySource, warningMessage);
      }

      // The requested file exists. Trigger a re-bundle to ensure there is no stale cache.
      await ensurePackageBundled(
        {
          name: packageName,
          version,
          shouldForce: true,
          shouldRerun: true,
          disableCache,
          shouldGetBundleInfo: config.logBundleInfo,
        },
        context,
      );
      // This will never be seen by the user, but it is needed to satisfy the response type.
      res.status(202).type('application/javascript').send('');
      return;
    }

    // If no entry was added, the import path could not resolve to a file in the package.
    const message = `Failed to resolve module specifier "${packageRequestPath}".`;
    // We need to pass the error to the bus so that it can be displayed in the UI,
    // and notify the user when autoUpdateEntries is enabled.
    bus.publish(errorEntrySource, message);
    return sendErrorResponse({ res, status: 404, requestPath, message });
  }

  // If we are accessing a non-bundled asset, return the file directly.
  if (!bundled) {
    return sendFile({
      res,
      fileFullPath: path.join(packageEntry.path, filePath),
      requestPath: packageRequestPath,
      parentPath: packageEntry.path,
      isBundled: false,
      shouldCache: !!packageEntry.isExternal,
    });
  }

  console.debug(`Bundle request: bundling "${packageName}@${version}"`);
  const { result } = await ensurePackageBundled(
    {
      name: packageName,
      version,
      shouldForce: force,
      shouldRerun: force,
      shouldWatch: config.mode !== 'production', // Only watch in non-production modes.
      disableCache,
      shouldGetBundleInfo: config.logBundleInfo,
    },
    context,
  );

  if (result.errors?.length || !result.outputPath) {
    // Bundling errors will be logged by ensurePackageBundled on bundle task completion.
    // The only case where they wouldn't be logged is if the user manually refreshes the page and
    // the same failed bundle task result is reused. There's not an easy way to detect that condition
    // (and the overlay and response details will show the error regardless), so only do a debug log
    // to avoid extra noise in the more common case.
    logResponse({
      status: 500,
      requestPath: packageRequestPath,
      message: 'Bundling failed (see error overlay or response data)',
      level: 'debug',
    });
    res.status(500).type('json').send(result);
    return;
  }

  // If the worker query param is not set but the file is a worker, handle it.
  if (
    features.enableModuleWorkers &&
    !worker &&
    // Currently, only webpack and rspack setup isWorker in the result.
    result.outputFiles?.find((file) => file.isWorker && file.outputPath === normalizeRelativePath(filePath))
  ) {
    await handleWorker(
      {
        req,
        res,
        workerFilePath: path.join(result.outputPath, filePath),
      },
      context,
    );
    return;
  }

  const fileFullPath = path.join(result.outputPath, filePath);

  // If rewriteImports query param is set, rewrite bare imports in the file
  // This is used for files imported by workers to ensure nested imports also work
  if (req.query.rewriteimports !== undefined && session.importMap) {
    try {
      const rewrittenSource = await rewriteImportsFromFile(fileFullPath, context);

      logResponse({ status: 200, requestPath: packageRequestPath, message: 'Returning file with rewritten imports' });
      // Do not cache these responses as they are based on the import map at request time.
      res.setHeader('Cache-Control', 'no-cache, no-store, must-revalidate');
      res.type('text/javascript').send(rewrittenSource);
      return;
    } catch (err) {
      const error = err as Error;
      console.error('Failed to rewrite imports for', packageRequestPath, ':', error);
      return sendErrorResponse({
        res,
        status: 500,
        requestPath: packageRequestPath,
        message: `Failed to rewrite imports: ${error.message}`,
      });
    }
  }

  sendFile({
    res,
    fileFullPath,
    requestPath: packageRequestPath,
    parentPath: result.outputPath,
    isBundled: true,
    shouldCache:
      result.isExternal ||
      (hash !== 'pending' &&
        // For internal packages, cache if the request hash matches the current source hash
        hash ===
          (await packageHashes.get({
            packagePath: packageEntry.path,
            isSourceHashingEnabled: true,
            targetEnvironment: 'browser',
          }))),
    onError: (err) => {
      // This is a workaround for the browser cache issue.
      // It seems to happen when a file is cached in the browser but the imported files are not cached and not on disk.
      // Increase the target version to clear browser cache for that specific package.
      session.incrementTargetVersion(packageEntry.path);

      // If the file is not found, we need to report it to telemetry.
      void telemetryClient.tracer.startActiveSpan('FILE_SERVE_ERROR', (span) => {
        span.setAttributes({
          isCacheHit: !!result.bundleSource && result.bundleSource !== 'bundler',
          isExternal: result.isExternal,
          packageName,
          packageVersion: version,
          bundleSource: result?.bundleSource,
          bundler: result?.bundler,
          bundlerVersion: result?.bundlerVersion,
          filePath,
          request: requestPath,
          hash: result.hash,
          // Actual FS errors will have a `code`, which is more informative than `statusCode`.
          // If `sendFile` manually returns an HTTP error, only `statusCode` is set.
          errorCode: String(err.code || err.statusCode || ''),
        });
      });
    },
  });
}

/** Debug log about the redirect and then send it */
function sendRedirect(params: { res: Response; requestPath: string; message: string; redirectUrl: string }): void {
  const { res, requestPath, message, redirectUrl } = params;
  logResponse({ status: 302, requestPath, message });
  res.redirect(redirectUrl);
}

/**
 * Do some error checks, then send the file.
 */
function sendFile(params: {
  res: Response;
  /** Output path for bundled files, package path for unbundled files */
  parentPath: string;
  /** Full path to the file to return */
  fileFullPath: string;
  requestPath: string;
  isBundled: boolean;
  shouldCache: boolean;
  /** Extra error handling such as telemetry */
  onError?: (err: ErrorWithCodes) => void;
}): void {
  const { res, fileFullPath, requestPath, parentPath, isBundled, shouldCache, onError } = params;

  // If the file path is outside of the package, return a 403 error.
  // This is a security measure to prevent access to files outside of the package.
  // (On Windows, path.relative() returns an absolute path for different drives.)
  const relativePath = path.relative(parentPath, fileFullPath);
  if (relativePath.startsWith('..') || path.isAbsolute(relativePath)) {
    sendErrorResponse({
      res,
      status: 403,
      requestPath,
      message: `Resolved file path is outside of the package`,
      consoleMessage: `File resolved to "${fileFullPath}" which is outside of "${parentPath}"`,
    });
    return;
  }

  console.debug(`Bundle response: ${isBundled ? 'bundled' : 'unbundled'} file "${requestPath}"`);

  // The below header is need to be able to track the browser cache hit ratio.
  res.setHeader('Timing-Allow-Origin', '*');

  // Express v5 default behavior is to ignore (404) anything with a leading dot in any path segment,
  // such as .cloudpack, so we have to explicitly allow it.
  res.sendFile(fileFullPath, { dotfiles: 'allow', maxAge: shouldCache ? maxAge : 0 }, (err) => {
    const errWithCode = err as ErrorWithCodes | undefined;
    // It appears this callback is called on success (even though the types don't indicate that),
    // so only continue if there's an error. Also ignore ECONNABORTED errors (don't log to console
    // or telemetry) since this probably means the user refreshed or closed the tab.
    if (!errWithCode || errWithCode.code === 'ECONNABORTED') {
      return;
    }

    // Send to telemetry if applicable
    onError?.(errWithCode);

    const status = errWithCode.statusCode || 500;
    let message = `Error serving file: ${errWithCode.message}`;

    if (errWithCode.code === 'ENOENT') {
      // If it's an actual file not found error (not something else where sendFile manually returns 404),
      // use a custom message since often it's due to a stale cache.
      message =
        `Request resolved to "${fileFullPath}" which does not exist. ` +
        'This might be due to an outdated browser cache, so please try reloading the page (or report the issue if it persists).';
    }

    sendErrorResponse({
      res,
      status,
      requestPath,
      message,
      consoleDetails: err.stack && `Bundle request error details: ${err.stack}`,
    });
  });
}
